import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, InputNumber, Layout, message, Row, Select, Space, Table, Typography } from "antd";
import {
    listFinancialYears,
    listSettlementOpeningBalances,
    setSettlementOpeningBalance,
    clearSettlementOpeningBalance
} from "../../actions/settlement.action";
import { showConfirm } from "../../components/custom-components/custom-component";

const { Title, Text } = Typography;
const { Content } = Layout;
const { Option } = Select;

const deriveCurrentFY = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // 1-12
    // FY runs Apr (month 4) - Mar (month 3 next year)
    const startYear = month >= 4 ? year : year - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
};

export const OpeningBalanceSeedPage = () => {
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedFY, setSelectedFY] = useState(deriveCurrentFY());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [edits, setEdits] = useState({});
    const [savingCode, setSavingCode] = useState(null);

    useEffect(() => {
        listFinancialYears(
            (data) => setFinancialYears(Array.isArray(data) ? data : []),
            (errMsg) => message.error(errMsg)
        );
    }, []);

    const fetchRows = (fy) => {
        if (!fy) return;
        setLoading(true);
        listSettlementOpeningBalances(
            { financialYear: fy },
            (data) => {
                setRows(Array.isArray(data) ? data : []);
                setEdits({});
                setLoading(false);
            },
            (errMsg) => {
                setLoading(false);
                message.error(errMsg);
            }
        );
    };

    useEffect(() => {
        fetchRows(selectedFY);
    }, [selectedFY]);

    const fyOptions = useMemo(() => {
        if (financialYears.length > 0) return financialYears.map((fy) => fy.financial_year);
        return [selectedFY];
    }, [financialYears, selectedFY]);

    const getEdit = (operatorCode) => edits[operatorCode] || {};

    const onAmountChange = (operatorCode, value) => {
        setEdits((prev) => ({ ...prev, [operatorCode]: { ...(prev[operatorCode] || {}), amount: value } }));
    };

    const onRemarksChange = (operatorCode, value) => {
        setEdits((prev) => ({ ...prev, [operatorCode]: { ...(prev[operatorCode] || {}), remarks: value } }));
    };

    const onSave = (record) => {
        const edit = getEdit(record.operator_code);
        const amount = edit.amount !== undefined ? edit.amount : record.amount;
        const remarks = edit.remarks !== undefined ? edit.remarks : record.remarks;
        if (amount === null || amount === undefined || amount === "") {
            message.warning("Enter an amount before saving");
            return;
        }
        setSavingCode(record.operator_code);
        setSettlementOpeningBalance(
            { operatorCode: record.operator_code, financialYear: selectedFY, amount, remarks },
            (_, msg) => {
                setSavingCode(null);
                message.success(msg || "Saved");
                fetchRows(selectedFY);
            },
            (errMsg) => {
                setSavingCode(null);
                message.error(errMsg);
            }
        );
    };

    const onClear = (record) => {
        setSavingCode(record.operator_code);
        clearSettlementOpeningBalance(
            { operatorCode: record.operator_code, financialYear: selectedFY },
            (_, msg) => {
                setSavingCode(null);
                message.success(msg || "Cleared");
                fetchRows(selectedFY);
            },
            (errMsg) => {
                setSavingCode(null);
                message.error(errMsg);
            }
        );
    };

    const columns = [
        {
            title: "Operator",
            dataIndex: "name",
            sorter: (a, b) => (a.name || "").localeCompare(b.name || "")
        },
        {
            title: "Operator #",
            dataIndex: "operator_number",
            width: 110
        },
        {
            title: "Current",
            dataIndex: "amount",
            width: 160,
            render: (amount) => {
                if (amount == null) return <Text type="secondary">— not set —</Text>;
                const numeric = Number(amount);
                const tag = numeric > 0
                    ? <Text type="success">credit</Text>
                    : numeric < 0
                        ? <Text type="danger">debit</Text>
                        : <Text>zero</Text>;
                return <span>{numeric.toFixed(2)} ({tag})</span>;
            }
        },
        {
            title: "Amount (signed)",
            dataIndex: "operator_code",
            width: 200,
            render: (operatorCode, record) => {
                const edit = getEdit(operatorCode);
                const value = edit.amount !== undefined
                    ? edit.amount
                    : (record.amount == null ? null : Number(record.amount));
                return (
                    <InputNumber
                        value={value}
                        onChange={(v) => onAmountChange(operatorCode, v)}
                        style={{ width: "100%" }}
                        placeholder="+ credit, - debit"
                        step={1}
                        precision={2}
                    />
                );
            }
        },
        {
            title: "Remarks",
            dataIndex: "operator_code",
            render: (operatorCode, record) => {
                const edit = getEdit(operatorCode);
                const value = edit.remarks !== undefined ? edit.remarks : (record.remarks || "");
                return (
                    <input
                        value={value}
                        onChange={(e) => onRemarksChange(operatorCode, e.target.value)}
                        style={{ width: "100%", padding: "4px 8px", border: "1px solid #d9d9d9", borderRadius: 4 }}
                        placeholder="Optional note"
                    />
                );
            }
        },
        {
            title: "Actions",
            key: "actions",
            width: 200,
            render: (_, record) => {
                const isSavingThisRow = savingCode === record.operator_code;
                const hasSeed = record.amount != null;
                return (
                    <Space>
                        <Button
                            type="primary"
                            loading={isSavingThisRow}
                            onClick={() => onSave(record)}
                        >
                            Save
                        </Button>
                        {hasSeed && (
                            <Button
                                danger
                                loading={isSavingThisRow}
                                onClick={() => showConfirm(
                                    () => onClear(record),
                                    "Clear opening balance",
                                    `Remove the opening balance for ${record.name} (FY ${selectedFY})?`
                                )}
                            >
                                Clear
                            </Button>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <div className="settlement-opening-balance-page settlement-admin" style={{ margin: "20px" }}>
            <Title level={4}>Settlement &mdash; Opening Balances</Title>
            <Alert
                style={{ marginBottom: 16 }}
                type="info"
                showIcon
                message="Set per-operator opening balance for a financial year."
                description={
                    <span>
                        <Text strong>Positive</Text> = we owe the operator coming in (credit);
                        {" "}<Text strong>negative</Text> = the operator owes us (debit).
                        {" "}When the first settlement of this FY is generated for an operator, this amount becomes the draft's opening balance.
                        {" "}For subsequent drafts, the prior settlement's outstanding carries forward automatically.
                    </span>
                }
            />
            <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                <Col>
                    <Text strong>Financial Year:</Text>
                </Col>
                <Col>
                    <Select
                        value={selectedFY}
                        onChange={setSelectedFY}
                        style={{ width: 140 }}
                    >
                        {fyOptions.map((fy) => (
                            <Option key={fy} value={fy}>{fy}</Option>
                        ))}
                    </Select>
                </Col>
            </Row>
            <Content>
                <Table
                    rowKey="operator_code"
                    dataSource={rows}
                    columns={columns}
                    loading={loading}
                    pagination={{ pageSize: 25 }}
                />
            </Content>
        </div>
    );
};
